import { create } from "zustand";

const STORAGE_KEY = "hippo.library";

export interface Highlight {
  id: string;
  text: string;
  comment: string;
  /** Color key (yellow | green | blue | pink | purple). Defaults to yellow. */
  color?: string;
  /** 0-based index of which occurrence of `text` in the section is marked. */
  occurrence?: number;
}

interface Persisted {
  bookmarks: string[];
  notes: Record<string, string>;
  highlights: Record<string, Highlight[]>;
}

function load(): Persisted {
  const empty: Persisted = { bookmarks: [], notes: {}, highlights: {} };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...empty, ...(JSON.parse(raw) as Persisted) };
  } catch {
    /* ignore */
  }
  return empty;
}

interface LibraryState extends Persisted {
  isBookmarked: (id: string) => boolean;
  toggleBookmark: (id: string) => void;
  getNote: (id: string) => string;
  setNote: (id: string, note: string) => void;
  getHighlights: (id: string) => Highlight[];
  addHighlight: (id: string, text: string, comment: string, color?: string, occurrence?: number) => void;
  removeHighlight: (id: string, hid: string) => void;
  /** Drop all study data (bookmark, note, highlights) for the given sub-session ids. */
  purge: (ids: string[]) => void;
}

export const useLibraryStore = create<LibraryState>((set, get) => {
  const persist = () => {
    const { bookmarks, notes, highlights } = get();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ bookmarks, notes, highlights }));
  };

  const toggleIn = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  return {
    ...load(),

    isBookmarked: (id) => get().bookmarks.includes(id),
    toggleBookmark: (id) => {
      set({ bookmarks: toggleIn(get().bookmarks, id) });
      persist();
    },

    getNote: (id) => get().notes[id] ?? "",
    setNote: (id, note) => {
      set({ notes: { ...get().notes, [id]: note } });
      persist();
    },

    getHighlights: (id) => get().highlights[id] ?? [],
    addHighlight: (id, text, comment, color, occurrence) => {
      const h: Highlight = { id: `${Date.now()}-${Math.floor(Math.random() * 1e6)}`, text, comment, color, occurrence };
      const list = [...(get().highlights[id] ?? []), h];
      set({ highlights: { ...get().highlights, [id]: list } });
      persist();
    },
    removeHighlight: (id, hid) => {
      const list = (get().highlights[id] ?? []).filter((h) => h.id !== hid);
      set({ highlights: { ...get().highlights, [id]: list } });
      persist();
    },
    purge: (ids) => {
      if (ids.length === 0) return;
      const drop = new Set(ids);
      const notes = { ...get().notes };
      const highlights = { ...get().highlights };
      for (const id of drop) {
        delete notes[id];
        delete highlights[id];
      }
      set({
        bookmarks: get().bookmarks.filter((b) => !drop.has(b)),
        notes,
        highlights,
      });
      persist();
    },
  };
});
